/**
 * AdminPartnerReach — full-page partner outreach console.
 *
 * Features:
 *   - Filterable table of all partners (search + quick-filters)
 *   - Per-row WhatsApp button → opens PartnerReachModal
 *   - Bulk-Reach: select multiple partners + pick template
 *     → opens N wa.me tabs sequentially (with delay to avoid popup blocking)
 *
 * Filters supported:
 *   - "Alle"            (default)
 *   - "Still seit 14d+" (no new lead in 14 days)
 *   - "Aktive Hunter"   (≥1 lead in last 14 days)
 *   - "0 Kunden"        (still warming up)
 *   - "Heisse Partner"  (≥3 customers)
 *   - "Kein Reach"      (never been contacted via the panel)
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, MessageCircle, Filter, RefreshCw, Zap, Users, AlertTriangle, X, BellOff, Bell } from "lucide-react";
import AdminTopNav from "../components/AdminTopNav";
import PartnerReachModal from "../components/admin/PartnerReachModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authHeader() {
  const t = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : "";
  return { "X-Admin-Token": t || "" };
}

const FILTERS = [
  { id: "all", label: "Alle" },
  { id: "due", label: "🔔 Empfehlung fällig" },
  { id: "silent", label: "Still seit 14d+" },
  { id: "active", label: "Aktive Hunter" },
  { id: "no_cust", label: "0 Kunden" },
  { id: "hot", label: "Heisse Partner" },
  { id: "no_reach", label: "Noch nie kontaktiert" },
];

const URGENCY_PILL = {
  high: "bg-rose-100 text-rose-700 border-rose-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminPartnerReach() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [modalPartner, setModalPartner] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [bulkTemplateId, setBulkTemplateId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [webinarStats, setWebinarStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/partner-reach/overview`, { headers: authHeader() });
      setRows(r.data.rows || []);
      // Fire-and-forget webinar reminder stats — non-blocking, banner just hidden if empty.
      axios.get(`${API}/admin/partner-reach/webinar-reminder-stats`, { headers: authHeader() })
        .then((rr) => setWebinarStats(rr.data))
        .catch(() => setWebinarStats(null));
      // Lazy-fetch the template catalogue once — used by both the modal
      // pre-select and the queue tiles to resolve template labels.
      if (templates.length === 0) {
        try {
          const tplR = await axios.get(`${API}/admin/partner-reach/templates`, { headers: authHeader() });
          setTemplates(tplR.data.templates || []);
        } catch { /* best-effort */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Liste laden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  // Manually dismiss/snooze the current suggestion for a partner. The
  // global cooldown then suppresses ALL suggestions for this partner
  // for the next 5 days, so the banner advances to the next person.
  const dismissSuggestion = async (partner) => {
    if (!partner?.suggestion) return;
    // Optimistic update — strip the suggestion immediately so the UI feels
    // snappy; we revert if the API call fails.
    const previous = partner.suggestion;
    setRows((xs) => xs.map((r) => (r.id === partner.id ? { ...r, suggestion: null } : r)));
    try {
      await axios.post(
        `${API}/admin/partner-reach/dismiss`,
        { partner_id: partner.id, template_id: previous.template_id },
        { headers: authHeader() },
      );
      toast.success(`Push für ${partner.name || partner.email} verworfen.`);
      // Re-fetch to also refresh `last_reach` cooldown timestamp.
      load();
    } catch (e) {
      setRows((xs) => xs.map((r) => (r.id === partner.id ? { ...r, suggestion: previous } : r)));
      toast.error(e?.response?.data?.detail || "Verwerfen fehlgeschlagen.");
    }
  };

  // Permanently opt a partner out of (or back into) automatic push
  // suggestions. Manual outreach via the modal still works either way.
  const togglePushOptOut = async (partner) => {
    const turningOff = !partner.reach_opt_out;
    const verb = turningOff ? "stumm schalten" : "wieder aktivieren";
    if (!window.confirm(
      `Auto-Pushes für "${partner.name || partner.email}" ${verb}?\n\n` +
      (turningOff
        ? "Du bekommst keine automatischen Empfehlungen mehr für diesen Partner. Manuelle Nachrichten gehen weiterhin."
        : "Auto-Pushes werden wieder generiert sobald ein Trigger zutrifft.")
    )) return;
    setRows((xs) => xs.map((r) => (r.id === partner.id ? {
      ...r, reach_opt_out: turningOff, suggestion: turningOff ? null : r.suggestion,
    } : r)));
    try {
      await axios.post(
        `${API}/admin/partner-reach/opt-out`,
        { partner_id: partner.id, opt_out: turningOff },
        { headers: authHeader() },
      );
      toast.success(turningOff
        ? `${partner.name || partner.email} bekommt keine Auto-Pushes mehr.`
        : `Auto-Pushes für ${partner.name || partner.email} wieder aktiv.`);
      load();
    } catch (e) {
      setRows((xs) => xs.map((r) => (r.id === partner.id ? { ...r, reach_opt_out: !turningOff } : r)));
      toast.error(e?.response?.data?.detail || "Opt-Out-Toggle fehlgeschlagen.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q) {
        const hay = `${r.name || ""} ${r.email || ""} ${r.slug || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filter === "silent") {
        if (r.days_since_last_lead === null) return true;  // never had a lead
        if (r.days_since_last_lead < 14) return false;
      } else if (filter === "active") {
        if (r.days_since_last_lead === null) return false;
        if (r.days_since_last_lead >= 14) return false;
      } else if (filter === "no_cust") {
        if ((r.customers_count || 0) > 0) return false;
      } else if (filter === "hot") {
        if ((r.customers_count || 0) < 3) return false;
      } else if (filter === "no_reach") {
        if (r.last_reach) return false;
      } else if (filter === "due") {
        if (!r.suggestion) return false;
      }
      return true;
    });
  }, [rows, search, filter]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setSelected(new Set(filtered.map((r) => r.id)));
  const clearSelection = () => setSelected(new Set());

  const runBulk = async () => {
    if (!bulkTemplateId) {
      toast.error("Bitte zuerst eine Vorlage auswählen.");
      return;
    }
    const targets = filtered.filter((r) => selected.has(r.id) && r.phone_e164);
    const skipped = filtered.filter((r) => selected.has(r.id) && !r.phone_e164).length;
    if (!targets.length) {
      toast.error("Keine der ausgewählten Partner haben eine Telefon-Nummer.");
      return;
    }
    if (!window.confirm(
      `${targets.length} WhatsApp-Tab${targets.length === 1 ? "" : "s"} öffnen?\n\n` +
      (skipped ? `${skipped} Partner werden übersprungen (keine Nummer).\n\n` : "") +
      `Hinweis: dein Browser könnte nach 3-5 Tabs Popups blockieren. ` +
      `Du musst die Pop-ups dann erlauben oder die Liste in zwei Wellen abarbeiten.`
    )) return;
    setBulkBusy(true);
    let ok = 0;
    for (const partner of targets) {
      try {
        const r = await axios.post(
          `${API}/admin/partner-reach/render`,
          { partner_id: partner.id, template_id: bulkTemplateId },
          { headers: authHeader() },
        );
        if (r.data.wa_url) {
          window.open(r.data.wa_url, "_blank", "noopener,noreferrer");
          await axios.post(
            `${API}/admin/partner-reach/log`,
            { partner_id: partner.id, template_id: bulkTemplateId, body: r.data.rendered },
            { headers: authHeader() },
          );
          ok += 1;
          // 800ms pause to give the browser time to process each window.open
          await new Promise((res) => setTimeout(res, 800));
        }
      } catch (e) {
        // Per-partner failure should not abort the batch.
        console.error("bulk send failed for partner", partner.id, e);
      }
    }
    setBulkBusy(false);
    setBulkOpen(false);
    clearSelection();
    toast.success(`${ok} WhatsApp-Tabs geöffnet · ${ok} im Log gespeichert.`);
    load();
  };

  const totalLeads = rows.reduce((acc, r) => acc + (r.leads_count || 0), 0);
  const totalCustomers = rows.reduce((acc, r) => acc + (r.customers_count || 0), 0);

  // Top-of-queue coaching pushes: top 3 highest-urgency partners. The
  // FIRST gets the prominent banner card; the next 2 stack below as
  // compact "next-up" tiles so the admin sees who's next without
  // scrolling through the table. After each send, load() re-runs the
  // suggestion engine and the queue advances automatically.
  const topQueue = useMemo(() => {
    const withSugg = rows.filter((r) => r.suggestion && r.phone_e164);
    if (!withSugg.length) return [];
    const order = { high: 0, medium: 1, low: 2 };
    return [...withSugg].sort((a, b) => {
      const ua = order[a.suggestion.urgency] ?? 9;
      const ub = order[b.suggestion.urgency] ?? 9;
      if (ua !== ub) return ua - ub;
      return (b.leads_count || 0) - (a.leads_count || 0);
    }).slice(0, 3);
  }, [rows]);
  const nextUp = topQueue[0] || null;
  // Count how many partners still need outreach in total (banner queue).
  const reachableSuggCount = useMemo(
    () => rows.filter((r) => r.suggestion && r.phone_e164).length,
    [rows],
  );
  const nextTemplate = nextUp ? templates.find((t) => t.id === nextUp.suggestion.template_id) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminTopNav />
      <div className="max-w-7xl mx-auto p-5 sm:p-8 space-y-6">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Partner-Reach</h1>
            <div className="text-sm text-slate-500 mt-1">
              {rows.length} Partner · {totalLeads} Leads gesamt · {totalCustomers} Kunden
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="partner-reach-reload"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-sm"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Neu laden
          </button>
        </div>

        {/* "Nächster Coaching-Push" — sticky banner that surfaces the
            most urgent partner needing outreach. Refreshes on every
            `load()` so it advances automatically after each send. */}
        {nextUp ? (
          <div
            className="al-card p-4 border-2 border-violet-200 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-white"
            data-testid="next-up-banner"
          >
            <div className="flex items-start gap-3 flex-wrap">
              <div className={`p-2 rounded-lg ${nextUp.suggestion.urgency === "high" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                <Zap className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-violet-700 bg-violet-100 rounded-full px-2 py-0.5">
                    Nächster Coaching-Push
                  </span>
                  {reachableSuggCount > 1 ? (
                    <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                      noch {reachableSuggCount - 1} weitere
                    </span>
                  ) : null}
                </div>
                <div className="font-bold text-slate-900 mt-1" data-testid="next-up-partner-name">
                  {nextUp.name || nextUp.email}
                  <span className="text-slate-400 font-normal text-sm ml-2 font-mono-tab">
                    {nextUp.phone}
                  </span>
                </div>
                <div className="text-sm text-slate-700 mt-0.5">
                  <strong>{nextTemplate?.label || nextUp.suggestion.template_id}</strong> — {nextUp.suggestion.reason}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setModalPartner({ ...nextUp, _preselect: nextUp.suggestion.template_id })}
                  data-testid="next-up-send"
                  className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white font-semibold text-sm ${
                    nextUp.suggestion.urgency === "high"
                      ? "bg-rose-600 hover:bg-rose-700"
                      : "bg-amber-600 hover:bg-amber-700"
                  }`}
                >
                  <MessageCircle className="h-4 w-4" />
                  Nachricht jetzt schreiben
                </button>
                <button
                  type="button"
                  onClick={() => dismissSuggestion(nextUp)}
                  data-testid="next-up-dismiss"
                  title="Push manuell verwerfen — schaltet diesen Vorschlag für 5 Tage stumm"
                  className="inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 text-sm border border-slate-200"
                >
                  <X className="h-4 w-4" />
                  <span className="sm:hidden">Verwerfen</span>
                </button>
              </div>
            </div>
          </div>
        ) : !loading && rows.length > 0 ? (
          <div
            className="al-card p-3 border border-emerald-200 bg-emerald-50/50 flex items-center gap-3 text-sm text-emerald-800"
            data-testid="next-up-banner-empty"
          >
            <Zap className="h-4 w-4" />
            <strong>Alles im grünen Bereich</strong> — aktuell wartet kein Partner auf einen WhatsApp-Push.
          </div>
        ) : null}

        {/* Queue preview — next 2 partners after the main banner. Lets the
            admin see who's coming so they can mentally batch their pushes
            without scrolling through the full table. */}
        {topQueue.length > 1 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="next-up-queue">
            {topQueue.slice(1).map((q) => {
              const tpl = templates.find((t) => t.id === q.suggestion.template_id);
              return (
                <div
                  key={q.id}
                  data-testid={`next-up-queue-${q.id}`}
                  className="al-card p-3 border border-slate-200 flex items-start gap-3 hover:border-violet-300 hover:bg-violet-50/30 transition-colors"
                >
                  <div className={`p-1.5 rounded-md ${q.suggestion.urgency === "high" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-600"}`}>
                    <Zap className="h-3.5 w-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
                      Danach
                    </div>
                    <div className="font-semibold text-slate-900 text-sm truncate">{q.name || q.email}</div>
                    <div className="text-xs text-slate-600 mt-0.5 truncate">
                      <strong>{tpl?.label || q.suggestion.template_id}</strong>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                      {q.suggestion.reason}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setModalPartner({ ...q, _preselect: q.suggestion.template_id })}
                    title="Direkt schreiben"
                    className="shrink-0 p-1.5 rounded-md hover:bg-violet-100 text-violet-700"
                  >
                    <MessageCircle className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Webinar reminder banner — fires when there's an upcoming webinar
            and we have partners we haven't reminded about it yet. */}
        {webinarStats?.webinar && webinarStats.missing_count > 0 ? (
          <div className="al-card p-4 border-2 border-rose-200 bg-gradient-to-r from-rose-50 to-amber-50">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="p-2 rounded-lg bg-rose-100 text-rose-700">
                <Zap className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900">
                  Nächstes Webinar: {webinarStats.webinar.title || "Live-Webinar"}
                </div>
                <div className="text-sm text-slate-700 mt-0.5">
                  Am {new Date(webinarStats.webinar.starts_at).toLocaleString("de-DE", { dateStyle: "long", timeStyle: "short" })}
                  {" — "}
                  <strong>{webinarStats.missing_count}</strong> von {webinarStats.total_partners} Partnern noch nicht erinnert.
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  // Pre-select all partners that haven't been reached for this webinar
                  const ids = new Set(webinarStats.missing.map((m) => m.id));
                  setSelected(ids);
                  setBulkTemplateId("webinar_invite");
                  setBulkOpen(true);
                }}
                data-testid="webinar-bulk-trigger"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-semibold text-sm"
              >
                <MessageCircle className="h-4 w-4" />
                {webinarStats.missing_count} Partner erinnern
              </button>
            </div>
          </div>
        ) : null}

        {/* Filters */}
        <div className="al-card p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, Email oder Slug suchen…"
                data-testid="partner-reach-search"
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-200 text-sm"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  data-testid={`partner-reach-filter-${f.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    filter === f.id
                      ? "bg-violet-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {selected.size > 0 ? (
            <div className="mt-3 flex flex-wrap items-center gap-3 p-2.5 rounded-lg bg-violet-50 border border-violet-200">
              <Zap className="h-4 w-4 text-violet-700" />
              <div className="text-sm text-violet-900 font-semibold">
                {selected.size} ausgewählt
              </div>
              <button
                type="button"
                onClick={() => setBulkOpen(true)}
                data-testid="partner-reach-bulk-open"
                className="ml-auto px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold"
              >
                Bulk-Reach starten
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="text-xs text-slate-600 hover:underline"
              >
                Auswahl löschen
              </button>
            </div>
          ) : null}
        </div>

        {/* Desktop table (≥ md). On smaller screens we render a card list
            below instead — horizontal scrolling on a wide table is awful on
            mobile because the Aktion column sits 7 columns to the right. */}
        <div className="al-card overflow-hidden hidden md:block">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="p-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((r) => selected.has(r.id))}
                    onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                    data-testid="partner-reach-select-all"
                  />
                </th>
                <th className="p-3 text-left">Partner</th>
                <th className="p-3 text-right">Leads</th>
                <th className="p-3 text-right">Kunden</th>
                <th className="p-3 text-left">Letzter Lead</th>
                <th className="p-3 text-left">Empfehlung</th>
                <th className="p-3 text-left">Letzte Nachricht</th>
                <th className="p-3 text-right w-32">Aktion</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-400">Lade…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-400">
                  <Filter className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  Keine Partner für diesen Filter.
                </td></tr>
              ) : filtered.map((p) => {
                const isSilent = p.days_since_last_lead !== null && p.days_since_last_lead >= 14;
                const noPhone = !p.phone_e164;
                const sugg = p.suggestion;
                return (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`partner-reach-row-${p.id}`}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected.has(p.id)}
                        onChange={() => toggleSelect(p.id)}
                        data-testid={`partner-reach-select-${p.id}`}
                      />
                    </td>
                    <td className="p-3">
                      <div className="font-semibold text-slate-900">{p.name || "—"}</div>
                      <div className="text-xs text-slate-500 font-mono-tab">{p.email}</div>
                      <div className="text-xs text-slate-400 font-mono-tab">
                        {p.phone || (noPhone ? "📵 keine Nummer" : "—")}
                      </div>
                    </td>
                    <td className="p-3 text-right font-mono-tab font-semibold text-slate-900">{p.leads_count}</td>
                    <td className="p-3 text-right font-mono-tab font-semibold text-slate-900">{p.customers_count}</td>
                    <td className="p-3 text-xs text-slate-600">
                      {p.last_lead_at ? (
                        <>
                          {new Date(p.last_lead_at).toLocaleDateString("de-DE", { year: "numeric", month: "2-digit", day: "2-digit" })}
                          {isSilent ? (
                            <div className="inline-flex items-center gap-1 text-amber-700 ml-1 text-[10px]">
                              <AlertTriangle className="h-3 w-3" /> vor {p.days_since_last_lead} Tagen
                            </div>
                          ) : null}
                        </>
                      ) : <span className="italic text-slate-400">noch keiner</span>}
                    </td>
                    <td className="p-3 text-xs">
                      {p.reach_opt_out ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5"
                          data-testid={`partner-reach-optout-badge-${p.id}`}
                          title="Auto-Pushes deaktiviert"
                        >
                          <BellOff className="h-3 w-3" />
                          Stumm
                        </span>
                      ) : sugg ? (
                        <div className={`inline-flex items-start gap-1 px-2 py-1 rounded-md border max-w-[240px] ${URGENCY_PILL[sugg.urgency] || URGENCY_PILL.low}`}
                          data-testid={`partner-reach-suggestion-${p.id}`}>
                          <div className="flex flex-col gap-0.5 flex-1">
                            <span className="font-bold text-[10px] uppercase tracking-wider">
                              {sugg.urgency === "high" ? "🔥 jetzt" : sugg.urgency === "medium" ? "🔔 fällig" : "Tipp"}
                            </span>
                            <span className="text-[11px] leading-tight">{sugg.reason}</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); dismissSuggestion(p); }}
                            data-testid={`partner-reach-dismiss-${p.id}`}
                            title="Push manuell verwerfen"
                            className="shrink-0 p-0.5 rounded hover:bg-black/10 opacity-50 hover:opacity-100"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : <span className="italic text-slate-300">—</span>}
                    </td>
                    <td className="p-3 text-xs text-slate-600">
                      {p.last_reach ? (
                        <>
                          {new Date(p.last_reach.sent_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                          <div className="text-[10px] text-slate-400">{p.last_reach.template_id}</div>
                        </>
                      ) : <span className="italic text-slate-400">noch nie</span>}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => setModalPartner({ ...p, _preselect: sugg?.template_id })}
                          disabled={noPhone}
                          data-testid={`partner-reach-open-${p.id}`}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold disabled:opacity-30 disabled:cursor-not-allowed ${
                            sugg
                              ? "bg-rose-600 hover:bg-rose-700 text-white"
                              : "bg-emerald-600 hover:bg-emerald-700 text-white"
                          }`}
                          title={noPhone ? "Keine Telefon-Nummer hinterlegt" : sugg ? sugg.reason : "WhatsApp-Nachricht"}
                        >
                          <MessageCircle className="h-3.5 w-3.5" /> Nachricht
                        </button>
                        <button
                          type="button"
                          onClick={() => togglePushOptOut(p)}
                          data-testid={`partner-reach-optout-${p.id}`}
                          title={p.reach_opt_out
                            ? "Auto-Pushes wieder aktivieren"
                            : "Diesen Partner für Auto-Pushes stumm schalten"}
                          className={`p-1.5 rounded-md border text-xs ${
                            p.reach_opt_out
                              ? "bg-rose-50 border-rose-200 text-rose-600 hover:bg-rose-100"
                              : "bg-white border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                          }`}
                        >
                          {p.reach_opt_out ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        {/* Mobile card list (< md). One card per partner — Aktion-Button is
            always visible and tap-target-sized; checkbox top-left for bulk
            selection; suggestion-pill stacks vertically. */}
        <div className="md:hidden space-y-3" data-testid="partner-reach-mobile-list">
          {loading ? (
            <div className="al-card p-8 text-center text-slate-400 text-sm">Lade…</div>
          ) : filtered.length === 0 ? (
            <div className="al-card p-8 text-center text-slate-400 text-sm">
              <Filter className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              Keine Partner für diesen Filter.
            </div>
          ) : filtered.map((p) => {
            const isSilent = p.days_since_last_lead !== null && p.days_since_last_lead >= 14;
            const noPhone = !p.phone_e164;
            const sugg = p.suggestion;
            return (
              <div
                key={p.id}
                className={`al-card p-3 border ${sugg?.urgency === "high" ? "border-rose-200" : "border-slate-200"}`}
                data-testid={`partner-reach-card-${p.id}`}
              >
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    className="mt-1 h-5 w-5 shrink-0"
                    data-testid={`partner-reach-mobile-select-${p.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">{p.name || "—"}</div>
                    <div className="text-xs text-slate-500 truncate">{p.email}</div>
                    <div className="text-xs text-slate-400 font-mono-tab truncate">
                      {p.phone || (noPhone ? "📵 keine Nummer" : "—")}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Leads / Kund.</div>
                    <div className="font-bold text-slate-900 font-mono-tab">
                      {p.leads_count} / {p.customers_count}
                    </div>
                    <button
                      type="button"
                      onClick={() => togglePushOptOut(p)}
                      data-testid={`partner-reach-mobile-optout-${p.id}`}
                      title={p.reach_opt_out ? "Auto-Pushes wieder aktivieren" : "Diesen Partner für Auto-Pushes stumm schalten"}
                      className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold rounded-md px-1.5 py-0.5 ${
                        p.reach_opt_out
                          ? "bg-rose-50 text-rose-700 hover:bg-rose-100"
                          : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                      }`}
                    >
                      {p.reach_opt_out ? <BellOff className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
                      {p.reach_opt_out ? "stumm" : "aktiv"}
                    </button>
                  </div>
                </div>

                {sugg ? (
                  <div
                    className={`mt-2 px-2 py-1.5 rounded-md border text-xs flex items-start gap-2 ${URGENCY_PILL[sugg.urgency] || URGENCY_PILL.low}`}
                    data-testid={`partner-reach-mobile-suggestion-${p.id}`}
                  >
                    <div className="flex-1">
                      <span className="font-bold text-[10px] uppercase tracking-wider mr-1">
                        {sugg.urgency === "high" ? "🔥 jetzt" : sugg.urgency === "medium" ? "🔔 fällig" : "Tipp"}
                      </span>
                      {sugg.reason}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); dismissSuggestion(p); }}
                      data-testid={`partner-reach-mobile-dismiss-${p.id}`}
                      title="Push manuell verwerfen"
                      className="shrink-0 p-1 -m-1 rounded hover:bg-black/10 text-current opacity-60 hover:opacity-100"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}

                <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-slate-500">
                  <div className="truncate">
                    {p.last_lead_at ? (
                      <>
                        Letzter Lead: {new Date(p.last_lead_at).toLocaleDateString("de-DE")}
                        {isSilent ? (
                          <span className="ml-1 text-amber-700">· vor {p.days_since_last_lead}d</span>
                        ) : null}
                      </>
                    ) : (
                      <span className="italic text-slate-400">noch kein Lead</span>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    {p.last_reach ? (
                      <span className="italic">
                        Msg {new Date(p.last_reach.sent_at).toLocaleDateString("de-DE")}
                      </span>
                    ) : (
                      <span className="italic text-slate-400">nie kontaktiert</span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setModalPartner({ ...p, _preselect: sugg?.template_id })}
                  disabled={noPhone}
                  data-testid={`partner-reach-mobile-open-${p.id}`}
                  className={`mt-3 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed ${
                    sugg
                      ? "bg-rose-600 hover:bg-rose-700 text-white"
                      : "bg-emerald-600 hover:bg-emerald-700 text-white"
                  }`}
                >
                  <MessageCircle className="h-4 w-4" />
                  {noPhone ? "Keine Telefon-Nummer" : sugg ? "Nachricht jetzt schreiben" : "WhatsApp schreiben"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {modalPartner ? (
        <PartnerReachModal
          partner={modalPartner}
          onClose={() => setModalPartner(null)}
          onSent={() => { setModalPartner(null); load(); }}
        />
      ) : null}

      {bulkOpen ? (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3"
          onClick={(e) => { if (e.target === e.currentTarget) setBulkOpen(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-violet-100 text-violet-700">
                <Users className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="font-bold text-slate-900">Bulk-Reach an {selected.size} Partner</div>
                <div className="text-xs text-slate-500">
                  Eine Vorlage — wir öffnen für jeden Partner einen WhatsApp-Tab.
                </div>
              </div>
            </div>
            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
              {templates.filter((t) => t.id !== "custom").map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setBulkTemplateId(t.id)}
                  data-testid={`partner-reach-bulk-tpl-${t.id}`}
                  className={`w-full text-left p-3 rounded-lg border transition-colors text-sm ${
                    bulkTemplateId === t.id
                      ? "bg-violet-50 border-violet-300 ring-2 ring-violet-200"
                      : "bg-white border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="font-semibold text-slate-900">{t.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.use_case}</div>
                </button>
              ))}
            </div>
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setBulkOpen(false)}
                disabled={bulkBusy}
                className="px-3 py-2 rounded-md bg-slate-200 hover:bg-slate-300 text-slate-800 text-sm"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={runBulk}
                disabled={bulkBusy || !bulkTemplateId}
                data-testid="partner-reach-bulk-run"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                {bulkBusy ? "läuft …" : `${selected.size} Tab(s) öffnen`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
